import { showConfirm } from '../components/ConfirmDialog';
import CountryInput from '../components/CountryInput';
import AirlineCompanyField from '../components/AirlineCompanyField';
import CardStack, { type StackCard } from '../components/CardStack';
import PasswordInput from '../components/PasswordInput';
import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfileContext } from '../hooks/useProfileContext';
import { useWriteGate } from '../lib/writeGate';
import { apiChangePassword, api2faStatus, api2faSetup, api2faEnable, api2faDisable, apiGetMyTeam, apiUpdateMyTeam, apiMyPassports, apiMyVisas, apiMyAirlines, apiExportMyData, apiDeleteMyAccount, apiAiCredits, apiGetSessions, apiRevokeSession, apiRevokeOtherSessions, apiBillingPortal, apiGetBillingPlans, apiBillingCreditPacks, apiBillingCreditCheckout, apiGetMyAvailability, apiCreateMyAvailability, apiDeleteMyAvailability, apiGetMySongCharts, apiGetMyCalendar, apiRotateMyCalendar, apiValidateMrz, docScanUrl, type TeamMemberDTO, type AvailabilityDTO, type AccountSession, type AiCreditsInfo, type BillingPlansInfo, type CreditPacksInfo, type MySongChartDTO, type MyCalendarLink } from '../api';
import AutoTextarea from '../components/AutoTextarea';
import DocScanField from '../components/DocScanField';
import MyTechnicalCard from '../components/MyTechnicalCard';
import { isShowcaseMode } from '../lib/saveStatus';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

// Pricing & billing live on the marketing site. The app only links to the
// Stripe Customer Portal (active subscription) or back to the site for plans.
const SITE_URL = 'https://bandzontour.com';

/** Self-service password change + own Team card + 2FA management. */

// Capitalize a plan slug ('solo' -> 'Solo') for display. Plan names are the
// same in EN and FR.
function planLabel(plan?: string): string {
  if (!plan) return '';
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

// The next monthly allowance reset date from a 'YYYY-MM' cycle: the first day
// of the month AFTER the current cycle (the allowance refills on that day).
function nextResetLabel(cycle?: string): string {
  if (!cycle || !/^\d{4}-\d{2}$/.test(cycle)) return '';
  const [y, m] = cycle.split('-').map(Number);
  const d = new Date(Date.UTC(y, m, 1)); // month after the cycle
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
/** One row per song (across the caller's bands) in the "My song charts" view,
 * with its per-instrument charts flattened for display. */
interface MyChartGroup {
  band_id: string;
  band_name?: string;
  song_id: string;
  song_title: string;
  instruments: { instrument: string; content: string }[];
}
/** Group /api/me/song-charts rows by (band, song) preserving order, so a song
 * with several charts renders once with each instrument underneath. */
function groupMyCharts(rows: MySongChartDTO[]): MyChartGroup[] {
  const out: MyChartGroup[] = [];
  const index = new Map<string, MyChartGroup>();
  for (const r of rows) {
    const key = `${r.band_id}:${r.song_id}`;
    let g = index.get(key);
    if (!g) {
      g = { band_id: r.band_id, band_name: r.band_name, song_id: r.song_id, song_title: r.song_title, instruments: [] };
      index.set(key, g);
      out.push(g);
    }
    g.instruments.push({ instrument: r.instrument, content: r.content });
  }
  return out;
}

export default function AccountPage() {
  const { user, logout } = useAuth();
  // Read-only SHOWCASE (demo) account: every write is already cut client-side
  // (api.ts rejects the mutation) and refused by the server (403), so a mutating
  // handler must bail out BEFORE its call — no 403 toast, no local red error and
  // no misleading success message. The flag is set by AuthContext off
  // /api/auth/me (`users.is_showcase`).
  // The "read-only" message itself comes from the app-wide <ShowcaseBanner/>
  // (mounted in App.tsx, top of every page) — do NOT duplicate it here.
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeProfileId } = useProfileContext();
  const writeGate = useWriteGate();
  // AI assistant access is EDITOR+ (viewer-surface v2, alpha.25): the AI credits
  // section only makes sense for the roles that can still use the assistant.
  // Same rule as the `aiAssistant` page (owner / editor / showcase) — the demo
  // account visits everything, so it keeps the section.
  const canUseAi = writeGate.canWriteEditor || isShowcaseMode() || !!user?.is_showcase;
  // Viewer reorg (2026-10-21): on the Account page a viewer gets its OWN identity
  // card ("My People card") FIRST, then the technical card — as two visually
  // separate cards. Editors/owners keep the historical single-card layout.
  // SPEC-TEAM-ACCOUNT-REORDER (2026-10-21): ONE section order for every role —
  // the viewer/editor split is gone (isViewer no longer drives the layout).
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  // ---- Own Team card state ----
  const [team, setTeam] = useState<Partial<TeamMemberDTO> | null>(null);
  const [teamLoaded, setTeamLoaded] = useState(false);
  const [teamMsg, setTeamMsg] = useState<string | null>(null);
  const [teamErr, setTeamErr] = useState<string | null>(null);
  const [teamSaving, setTeamSaving] = useState(false);

  // ---- My calendar state (SPEC-PERSONAL-CALENDAR, 2026-10-21) ----
  const [calLink, setCalLink] = useState<MyCalendarLink | null>(null);
  const [calCopied, setCalCopied] = useState(false);
  const [calRotating, setCalRotating] = useState(false);

  // ---- Own availability (unavailability declared per band, V1) ----
  const [availBands, setAvailBands] = useState<{ id: string; name: string }[]>([]);
  const [avail, setAvail] = useState<AvailabilityDTO[]>([]);
  const [availLoaded, setAvailLoaded] = useState(false);
  const [availMsg, setAvailMsg] = useState<string | null>(null);
  const [availErr, setAvailErr] = useState<string | null>(null);
  const [availSaving, setAvailSaving] = useState(false);
  // Add-form fields.
  const [newBand, setNewBand] = useState('');
  const [newFrom, setNewFrom] = useState('');
  const [newTo, setNewTo] = useState('');
  const [newReason, setNewReason] = useState('');

  // ---- My song charts (Smart Setlist V2, per-account view) ----
  const [myCharts, setMyCharts] = useState<MySongChartDTO[]>([]);
  const [myChartsLoaded, setMyChartsLoaded] = useState(false);

  // ---- 2FA state ----
  const [twofa, setTwofa] = useState<{ enabled: boolean } | null>(null);
  const [setup, setSetup] = useState<{ secret: string; otpauth_url: string; backup_codes: string[] } | null>(null);
  const [code, setCode] = useState('');
  const [twofaMsg, setTwofaMsg] = useState<string | null>(null);
  const [twofaErr, setTwofaErr] = useState<string | null>(null);

  // ---- AI credits (metering) ---- (2 pools: allowance + top-up + plan)
  const [aiCredits, setAiCredits] = useState<AiCreditsInfo | null>(null);

  // ---- One-time credit top-up packs (Stripe mode: payment) ----
  const [creditPacks, setCreditPacks] = useState<CreditPacksInfo | null>(null);
  const [buyingPack, setBuyingPack] = useState<string | null>(null);
  const [topupMsg, setTopupMsg] = useState<string | null>(null);
  const [topupErr, setTopupErr] = useState<string | null>(null);

  // ---- Subscription status (discreet) ---- (current plan + portal/site link)
  const [billing, setBilling] = useState<BillingPlansInfo | null>(null);
  const [billingErr, setBillingErr] = useState<string | null>(null);

  // ---- Active sessions / devices (remote logout) ----
  const [sessions, setSessions] = useState<AccountSession[]>([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [sessionsMsg, setSessionsMsg] = useState<string | null>(null);
  const [sessionsErr, setSessionsErr] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  const loadSessions = async () => {
    setSessionsErr(null);
    try {
      setSessions(await apiGetSessions());
    } catch (err) {
      setSessionsErr(err instanceof Error ? err.message : 'Could not load sessions.');
    } finally {
      setSessionsLoaded(true);
    }
  };

  const logoutAllOtherDevices = async () => {
    if (isShowcaseMode()) return;
    if (!(await showConfirm(t('account.logoutAllOtherConfirm')))) return;
    setRevoking(true); setSessionsErr(null); setSessionsMsg(null);
    try {
      await apiRevokeOtherSessions();
      setSessionsMsg(t('account.loggedOutOther'));
      await loadSessions();
    } catch (err) {
      setSessionsErr(err instanceof Error ? err.message : 'Could not sign out other devices.');
    } finally {
      setRevoking(false);
    }
  };

  const logoutSession = async (id: string) => {
    if (isShowcaseMode()) return;
    if (!(await showConfirm(t('account.logoutSessionConfirm')))) return;
    setRevoking(true); setSessionsErr(null); setSessionsMsg(null);
    try {
      await apiRevokeSession(id);
      setSessionsMsg(t('account.loggedOutSession'));
      await loadSessions();
    } catch (err) {
      setSessionsErr(err instanceof Error ? err.message : 'Could not sign out this session.');
    } finally {
      setRevoking(false);
    }
  };

  // Format a session created_at like "2026-08-23 09:11:37" (UTC) into a
  // localized date + time for display.
  const formatSessionDate = (iso: string): string => {
    if (!iso) return '';
    const d = new Date(iso.length === 19 ? iso.replace(' ', 'T') + 'Z' : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  useEffect(() => {
    api2faStatus().then(setTwofa).catch(() => setTwofa({ enabled: false }));
    // My calendar link (SPEC-PERSONAL-CALENDAR): created on first read, so the
    // block ALWAYS shows a usable subscription URL.
    apiGetMyCalendar().then(setCalLink).catch(() => setCalLink(null));
    apiGetMyTeam()
      .then((m) => { setTeam(m); setTeamLoaded(true); })
      .catch(() => { setTeamLoaded(true); });
    apiGetMyAvailability()
      .then((d) => {
        setAvailBands(d.bands || []);
        setAvail(d.availability || []);
        // Preselect the active band when the caller is multi-band.
        setNewBand((prev) => prev || ((d.bands || [])[0]?.id ?? ''));
        setAvailLoaded(true);
      })
      .catch(() => { setAvailLoaded(true); });
    // AI credits / top-up packs: editor+ only (viewer-surface v2) — a role
    // without AI access makes NO credits call at mount (clean network, no 403).
    if (canUseAi) {
      apiAiCredits()
        .then((d) => setAiCredits(d))
        .catch(() => setAiCredits(null));
      apiBillingCreditPacks()
        .then((d) => setCreditPacks(d))
        .catch(() => setCreditPacks(null));
    }
    apiGetBillingPlans()
      .then((d) => setBilling(d))
      .catch(() => setBilling(null));
    loadSessions();
    // A checkout return: refresh the balance and surface a short status line.
    const q = new URLSearchParams(window.location.search);
    if (q.get('topup') === 'success') setTopupMsg(t('billing.topupSuccess'));
    else if (q.get('topup') === 'cancel') setTopupMsg(t('billing.topupCancel'));
  }, [t, canUseAi]);

  // ---- My song charts (Smart Setlist V2) — charts for the instruments the
  // linked Team member plays, scoped to the ACTIVE band's songbook when one is
  // selected, else every band the member can access. Read-only. ----
  useEffect(() => {
    let active = true;
    if (!team?.id) { setMyCharts([]); setMyChartsLoaded(true); return; }
    setMyChartsLoaded(false);
    apiGetMySongCharts(activeProfileId || undefined)
      .then((d) => { if (active) { setMyCharts(d || []); setMyChartsLoaded(true); } })
      .catch(() => { if (active) { setMyCharts([]); setMyChartsLoaded(true); } });
    return () => { active = false; };
  }, [team?.id, activeProfileId]);

  // Buy a one-time credit pack -> create the Stripe Checkout session and
  // redirect the user to the hosted payment page.
  const buyCredits = async (packId: string) => {
    if (isShowcaseMode()) return;
    setTopupErr(null); setTopupMsg(null); setBuyingPack(packId);
    try {
      const { url } = await apiBillingCreditCheckout(packId);
      window.location.href = url;
    } catch (err) {
      setBuyingPack(null);
      setTopupErr(err instanceof Error ? err.message : t('billing.checkoutFailed'));
    }
  };

  // Format a pack price (minor units, e.g. 900 = 9.00) with its currency.
  const formatPackPrice = (p: { amount: number; currency: string }): string => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: p.currency.toUpperCase() })
        .format(p.amount / 100);
    } catch {
      return `${(p.amount / 100).toFixed(2)} ${p.currency.toUpperCase()}`;
    }
  };

  const teamField = (k: keyof TeamMemberDTO) => ({
    value: (team?.[k] as string) || '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTeam((p) => ({ ...p, [k]: e.target.value })),
  });

  const saveTeam = async () => {
    if (isShowcaseMode()) return;
    if (!team?.id) return;
    setTeamErr(null); setTeamMsg(null); setTeamSaving(true);
    try {
      const saved = await apiUpdateMyTeam(team);
      setTeam(saved);
      setTeamMsg(t('account.detailsSaved'));
    } catch (err) {
      setTeamErr(err instanceof Error ? err.message : t('saveError.notSaved'));
    } finally {
      setTeamSaving(false);
    }
  };

  // ---- Own availability handlers ----
  const addAvailability = async () => {
    if (isShowcaseMode()) return;
    setAvailErr(null); setAvailMsg(null);
    if (!newBand) { setAvailErr(t('availability.selectBand')); return; }
    if (!newFrom || !newTo) { setAvailErr(t('availability.selectDates')); return; }
    if (newTo < newFrom) { setAvailErr(t('availability.toBeforeFrom')); return; }
    setAvailSaving(true);
    try {
      const created = await apiCreateMyAvailability({
        band_id: newBand,
        date_from: newFrom,
        date_to: newTo,
        reason: newReason,
      });
      setAvail((prev) => [...prev, created]);
      setNewFrom(''); setNewTo(''); setNewReason('');
      setAvailMsg(t('availability.added'));
    } catch (err) {
      setAvailErr(err instanceof Error ? err.message : t('availability.addFailed'));
    } finally {
      setAvailSaving(false);
    }
  };

  const removeAvailability = async (a: AvailabilityDTO) => {
    if (isShowcaseMode()) return;
    const label = a.band_name ? `${a.date_from} → ${a.date_to} (${a.band_name})` : `${a.date_from} → ${a.date_to}`;
    if (!(await showConfirm(t('availability.deleteConfirm', { range: label })))) return;
    try {
      await apiDeleteMyAvailability(a.id);
      setAvail((prev) => prev.filter((x) => x.id !== a.id));
      setAvailMsg(t('availability.deleted'));
    } catch (err) {
      setAvailErr(err instanceof Error ? err.message : t('availability.deleteFailed'));
    }
  };

  // ---- Own documents state (passports / visas / loyalty) ----
  const [passports, setPassports] = useState<any[]>([]);
  const [visas, setVisas] = useState<any[]>([]);
  const [airlines, setAirlines] = useState<any[]>([]);
  const [docsLoaded, setDocsLoaded] = useState(false);
  const [docsMsg, setDocsMsg] = useState<string | null>(null);
  const [docsErr, setDocsErr] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch the caller's travel documents when a Team card is linked. The
    // backend 404s ("No Team card linked to this account") otherwise, spamming
    // the console. When there is no card we just mark the section loaded/empty.
    if (!team?.id) { setPassports([]); setVisas([]); setAirlines([]); setDocsLoaded(true); return; }
    Promise.all([
      apiMyPassports.list().catch(() => []),
      apiMyVisas.list().catch(() => []),
      apiMyAirlines.list().catch(() => []),
    ]).then(([p, v, a]) => {
      setPassports(p); setVisas(v); setAirlines(a); setDocsLoaded(true);
    }).catch(() => setDocsLoaded(true));
  }, [team?.id]);

  const docErr = (err: unknown) => setDocsErr(err instanceof Error ? err.message : 'Failed to save.');

  const addPassport = async () => {
    if (isShowcaseMode()) return;
    try { const p = await apiMyPassports.create({ passport_number: '', nationality: '', expiry_date: '' }); setPassports((prev) => [...prev, p]); setDocsErr(null); }
    catch (err) { docErr(err); }
  };
  const patchPassport = async (docId: string, patch: any) => {
    if (isShowcaseMode()) return;
    setPassports((prev) => prev.map((p) => (p.id === docId ? { ...p, ...patch } : p)));
    try { const current = passports.find((p) => p.id === docId); await apiMyPassports.update(docId, { ...current, ...patch }); setDocsMsg(t('account.passportSaved')); }
    catch (err) { docErr(err); }
  };
  const removePassport = async (docId: string) => {
    if (isShowcaseMode()) return;
    if (!(await showConfirm(t('account.deletePassportConfirm')))) return;
    try { await apiMyPassports.remove(docId); setPassports((prev) => prev.filter((p) => p.id !== docId)); setDocsErr(null); }
    catch (err) { docErr(err); }
  };

  // ---- Passport MRZ auto-fill on the SELF-SERVICE card (Account → Documents,
  // 2026-10-21): same option-B flow as TeamEditPage — the OCR runs ENTIRELY in
  // this browser (src/lib/mrzOcr.ts, lazy tesseract.js) and only the two raw
  // MRZ text lines are validated by the self-service route. The result is a
  // PROPOSAL held in LOCAL state (ocrDraft): EMPTY fields only are pre-filled,
  // nothing is written until the user applies the draft through the EXISTING
  // save path (patchPassport -> apiMyPassports.update).
  type OcrDraft = { method: 'mrz'; fields: Partial<Record<'passport_number' | 'nationality' | 'issuing_country' | 'expiry_date' | 'issue_date', string>> };
  const [ocrDraft, setOcrDraft] = useState<Record<string, OcrDraft | undefined>>({});
  const [ocrBusy, setOcrBusy] = useState<Record<string, boolean>>({});
  const [ocrMsg, setOcrMsg] = useState<Record<string, string>>({});

  const passportValue = (p: any, key: 'passport_number' | 'nationality' | 'issuing_country' | 'expiry_date' | 'issue_date'): string => {
    const draft = ocrDraft[p.id]?.fields[key];
    return (draft !== undefined && draft !== null ? draft : (p[key] || ''));
  };
  const clearDraftField = (docId: string, key: string) => {
    setOcrDraft((prev) => {
      const d = prev[docId];
      if (!d) return prev;
      const fields = { ...d.fields } as Record<string, unknown>;
      delete fields[key];
      return { ...prev, [docId]: { ...d, fields: fields as OcrDraft['fields'] } };
    });
  };
  const patchPassportField = (p: any, key: 'passport_number' | 'nationality' | 'issuing_country' | 'expiry_date' | 'issue_date', value: string) => {
    if (ocrDraft[p.id]?.fields[key] !== undefined) clearDraftField(p.id, key);
    patchPassport(p.id, { [key]: value });
  };
  const autoFillPassport = async (p: any) => {
    setOcrBusy((prev) => ({ ...prev, [p.id]: true }));
    setOcrMsg((prev) => ({ ...prev, [p.id]: '' }));
    try {
      // The scan bytes come from the GUARDED self-service /scan route and are
      // decoded locally — the image never leaves the machine.
      const res = await fetch(docScanUrl('passport', p.id, { self: true }), { credentials: 'include' });
      if (!res.ok) throw new Error('scan_unavailable');
      const blob = await res.blob();
      const { extractMrzLines } = await import('../lib/mrzOcr');
      const lines = await extractMrzLines(blob);
      const r = await apiValidateMrz('', p.id, lines[0] || '', lines[1] || '', { self: true });
      if (r.ok && r.proposal) {
        // Pre-fill EMPTY fields only — never overwrite a typed value.
        // issue_date is an ESTIMATE (expiry − 10 y / − 5 y under 15), NOT an
        // MRZ extraction: it is labeled "estimated" on the field.
        const fields: OcrDraft['fields'] = {};
        for (const key of ['passport_number', 'nationality', 'issuing_country', 'expiry_date', 'issue_date'] as const) {
          const proposed = String(r.proposal[key] || '').trim();
          const existing = String(p[key] || '').trim();
          if (proposed && !existing) fields[key] = proposed;
        }
        if (Object.keys(fields).length === 0) {
          setOcrMsg((prev) => ({ ...prev, [p.id]: t('teamEdit.autoFillNoEmpty') }));
        } else {
          setOcrDraft((prev) => ({ ...prev, [p.id]: { method: 'mrz', fields } }));
        }
      } else {
        setOcrMsg((prev) => ({ ...prev, [p.id]: r.error || t('teamEdit.autoFillFail') }));
      }
    } catch {
      setOcrMsg((prev) => ({ ...prev, [p.id]: t('teamEdit.autoFillFail') }));
    } finally {
      setOcrBusy((prev) => ({ ...prev, [p.id]: false }));
    }
  };
  // ONE merged patch — one PUT for the whole draft (never one per field: the
  // per-field loop was racy and dropped earlier fields, measured 2026-10-21).
  const applyPassportDraft = (p: any) => {
    const draft = ocrDraft[p.id];
    if (!draft) return;
    const patch: Record<string, string> = {};
    for (const [key, value] of Object.entries(draft.fields)) {
      if (value !== undefined && value !== null) patch[key] = value;
    }
    patchPassport(p.id, patch);
    setOcrDraft((prev) => ({ ...prev, [p.id]: undefined }));
    setOcrMsg((prev) => ({ ...prev, [p.id]: '' }));
  };
  const discardPassportDraft = (docId: string) => {
    setOcrDraft((prev) => ({ ...prev, [docId]: undefined }));
    setOcrMsg((prev) => ({ ...prev, [docId]: '' }));
  };


  const addVisa = async () => {
    if (isShowcaseMode()) return;
    try { const v = await apiMyVisas.create({ country: '', visa_type: '', visa_number: '', expiry_date: '', entry_type: 'single' }); setVisas((prev) => [...prev, v]); setDocsErr(null); }
    catch (err) { docErr(err); }
  };
  const patchVisa = async (docId: string, patch: any) => {
    if (isShowcaseMode()) return;
    setVisas((prev) => prev.map((v) => (v.id === docId ? { ...v, ...patch } : v)));
    try { const current = visas.find((v) => v.id === docId); await apiMyVisas.update(docId, { ...current, ...patch }); setDocsMsg(t('account.visaSaved')); }
    catch (err) { docErr(err); }
  };
  const removeVisa = async (docId: string) => {
    if (isShowcaseMode()) return;
    if (!(await showConfirm(t('account.deleteVisaConfirm')))) return;
    try { await apiMyVisas.remove(docId); setVisas((prev) => prev.filter((v) => v.id !== docId)); setDocsErr(null); }
    catch (err) { docErr(err); }
  };

  const addAirline = async () => {
    if (isShowcaseMode()) return;
    try { const a = await apiMyAirlines.create({ company: '', program_number: '', tier: '' }); setAirlines((prev) => [...prev, a]); setDocsErr(null); }
    catch (err) { docErr(err); }
  };
  const patchAirline = async (docId: string, patch: any) => {
    if (isShowcaseMode()) return;
    setAirlines((prev) => prev.map((a) => (a.id === docId ? { ...a, ...patch } : a)));
    try { const current = airlines.find((a) => a.id === docId); await apiMyAirlines.update(docId, { ...current, ...patch }); setDocsMsg(t('account.loyaltySaved')); }
    catch (err) { docErr(err); }
  };
  const removeAirline = async (docId: string) => {
    if (isShowcaseMode()) return;
    if (!(await showConfirm(t('account.deleteLoyaltyConfirm')))) return;
    try { await apiMyAirlines.remove(docId); setAirlines((prev) => prev.filter((a) => a.id !== docId)); setDocsErr(null); }
    catch (err) { docErr(err); }
  };

  async function submit(e: React.FormEvent) {
    if (isShowcaseMode()) return;
    e.preventDefault();
    setError(null);
    if (next !== confirm) { setError(t('account.passwordsDontMatch')); return; }
    setBusy(true);
    try {
      await apiChangePassword(current, next);
      setDone(true);
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  }

  // ---- GDPR data-portability export ----
  const [gdprBusy, setGdprBusy] = useState(false);
  const [gdprMsg, setGdprMsg] = useState<string | null>(null);
  const [gdprErr, setGdprErr] = useState<string | null>(null);

  async function exportMyData() {
    setGdprBusy(true); setGdprMsg(null); setGdprErr(null);
    try {
      const blob = await apiExportMyData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `bandz-data-export-${user?.username || 'me'}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setGdprMsg(t('account.dataDownloaded'));
    } catch (err) {
      setGdprErr(err instanceof Error ? err.message : 'Could not export your data.');
    } finally {
      setGdprBusy(false);
    }
  }

  // ---- GDPR right to erasure (delete my account) ----
  const [eraseBusy, setEraseBusy] = useState(false);
  const [eraseErr, setEraseErr] = useState<string | null>(null);
  const [eraseConfirm, setEraseConfirm] = useState('');

  async function deleteMyAccount() {
    if (isShowcaseMode()) return;
    if (eraseConfirm !== (user?.username || '')) {
      setEraseErr(t('account.eraseConfirmMismatch'));
      return;
    }
    if (!(await showConfirm(t('account.eraseConfirm'), { title: user?.username || '', danger: true, confirmLabel: t('account.eraseBtn') }))) return;
    setEraseBusy(true); setEraseErr(null);
    try {
      await apiDeleteMyAccount();
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      setEraseErr(err instanceof Error ? err.message : t('account.eraseFailed'));
      setEraseBusy(false);
    }
  }

  async function startSetup() {
    if (isShowcaseMode()) return;
    setTwofaErr(null); setTwofaMsg(null); setSetup(null);
    try {
      const s = await api2faSetup();
      setSetup(s);
    } catch (err) {
      setTwofaErr(err instanceof Error ? err.message : 'Could not start 2FA setup.');
    }
  }

  async function enable2fa() {
    if (isShowcaseMode()) return;
    setTwofaErr(null);
    if (!code) { setTwofaErr(t('account.enterCode')); return; }
    try {
      await api2faEnable(code);
      setSetup(null); setCode(''); setTwofa({ enabled: true });
      setTwofaMsg(t('account.twofaEnabledMsg'));
    } catch (err) {
      setTwofaErr(err instanceof Error ? err.message : t('account.invalidCode'));
    }
  }

  async function disable2fa() {
    if (isShowcaseMode()) return;
    setTwofaErr(null);
    if (!code) { setTwofaErr(t('account.enterCodeDisable')); return; }
    try {
      await api2faDisable(code);
      setCode(''); setTwofa({ enabled: false });
      setTwofaMsg(t('account.twofaDisabledMsg'));
    } catch (err) {
      setTwofaErr(err instanceof Error ? err.message : t('account.invalidCode'));
    }
  }

  async function openBillingPortal() {
    if (isShowcaseMode()) return;
    setBillingErr(null);
    try {
      const { url } = await apiBillingPortal();
      window.location.href = url;
    } catch (err) {
      setBillingErr(err instanceof Error ? err.message : t('billing.portalFailed'));
    }
  }

  // ---- My calendar (SPEC-PERSONAL-CALENDAR, 2026-10-21) ----
  async function copyCalendarLink() {
    if (!calLink) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${calLink.url}`);
      setCalCopied(true);
      setTimeout(() => setCalCopied(false), 2000);
    } catch { /* clipboard refused — the URL stays visible below */ }
  }

  // Rotate = the OLD token dies: external subscriptions using it start getting
  // 403 and must re-subscribe with the fresh link.
  async function rotateCalendarLink() {
    if (isShowcaseMode() || calRotating) return;
    setCalRotating(true);
    try { setCalLink(await apiRotateMyCalendar()); } catch { /* keep the current one */ }
    setCalRotating(false);
  }

  const billingSub = billing?.subscription || null;
  const hasActiveBilling = !!billingSub && (billingSub.status === 'active' || billingSub.status === 'past_due');
  const billingTier = hasActiveBilling ? (billing?.tiers || []).find((t) => t.id === billingSub!.plan) : null;

  // ---- Blocks (viewer reorg, 2026-10-21) ----
  // My People card = the caller's own identity (name, passport, diet…); the
  // technical card = instruments / backline / IEM. TWO separate cards: a viewer
  // gets identity FIRST, editors/owners keep the historical single-card layout
  // (technical card first). The markup is shared — only its placement changes.
  const teamCardSection = (
    <>
  {teamLoaded && team == null && (
  <p style={{ color: 'var(--text-dim)' }}>
  {t('account.noTeamCard')}
  </p>
  )}

  {teamLoaded && team != null && (
  <>
  {/* SPEC-TEAM-ACCOUNT-REORDER (2026-10-21): identity header replicating the
      Team page People card (same h3/role/details markup) so the Account card
      reads like the roster card; the self-service EDIT FORM below is unchanged. */}
  <div className="account-people-identity" style={{ marginBottom: 14 }} data-people-identity>
    <h3 style={{ margin: '0 0 2px' }}>{(team as any).nickname || (team as any).name}</h3>
    {((team as any).nickname && ((team as any).real_name || (team as any).last_name)) ? (
      <p className="musician-role" style={{ fontSize: '0.85rem', marginTop: -6, opacity: 0.75 }}>
        {[(team as any).real_name, (team as any).last_name].filter(Boolean).join(' ')}
      </p>
    ) : null}
    <p className="musician-role">{(team as any).category || t('team.noRole')}</p>
    <div className="musician-details">
      {(team as any).email && <span>✉️ {(team as any).email}</span>}
      {(team as any).phone && <span>📞 {(team as any).phone}</span>}
    </div>
    <div className="musician-details">
      {(((team as any).passports?.length || 0) > 0) && <span>🛂 {t(((team as any).passports.length > 1 ? 'team.passports_other' : 'team.passports'), { count: (team as any).passports.length })}</span>}
      {(((team as any).visas?.length || 0) > 0) && <span>📄 {t(((team as any).visas.length > 1 ? 'team.visas_other' : 'team.visas'), { count: (team as any).visas.length })}</span>}
    </div>
  </div>
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
  <div className="form-row">
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.usualName')}</label>
  <input className="form-input" {...teamField('name')} placeholder={t('account.nameUsedInBand')} />
  <small style={{ color: 'var(--text-dim)' }}>{t('account.displayNameHint')}</small>
  </div>
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.nickname')}</label>
  <input className="form-input" {...teamField('nickname')} placeholder={t('account.stageNamePh')} />
  </div>
  </div>
  <div className="form-row">
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.email')}</label>
  <input className="form-input" type="text" {...teamField('email')} />
  </div>
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.phone')}</label>
  <input className="form-input" {...teamField('phone')} />
  </div>
  </div>
  <div className="form-row">
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.dateOfBirth')}</label>
  <input className="form-input" {...teamField('date_of_birth')} placeholder={t('account.datePh')} />
  </div>
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.nationality')}</label>
  <input className="form-input" {...teamField('nationality')} />
  </div>
  </div>
  <div className="form-row">
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.address')}</label>
  <input className="form-input" {...teamField('address')} />
  </div>
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.postCodeLocality')}</label>
  <input className="form-input" {...teamField('locality')} placeholder={t('account.cityPh')} />
  </div>
  </div>
  <div className="form-row">
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.emergencyContact')}</label>
  <input className="form-input" {...teamField('emergency_contact_name')} />
  </div>
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.emergencyPhone')}</label>
  <input className="form-input" {...teamField('emergency_contact_phone')} />
  </div>
  </div>
  </div>

  <div className="form-row">
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.legalFirstName')}</label>
  <input className="form-input" {...teamField('real_name')} placeholder={t('account.asOnPassport')} />
  </div>
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.legalLastName')}</label>
  <input className="form-input" {...teamField('last_name')} />
  </div>
  </div>
  <div className="form-row">
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.postCode')}</label>
  <input className="form-input" {...teamField('post_code')} />
  </div>
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.country')}</label>
  <input className="form-input" {...teamField('country')} />
  </div>
  </div>
  <div className="form-row">
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.diet')}</label>
  <input className="form-input" {...teamField('diet')} placeholder={t('account.dietPh')} />
  </div>
  <div className="form-group" style={{ flex: 1 }}>
  <label>{t('account.dietNotes')}</label>
  <input className="form-input" {...teamField('diet_notes')} />
  </div>
  </div>

  {teamMsg && <div style={{ color: 'var(--success)', marginTop: 10 }}>{teamMsg}</div>}
  {teamErr && <div style={{ color: 'var(--danger)', fontSize: '0.9rem', marginTop: 10 }}>{teamErr}</div>}
  <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={saveTeam} disabled={teamSaving}>
  {teamSaving ? t('account.saving') : t('account.saveMyDetails')}
  </button>
  </>
  )}
    </>
  );
  // BARE content: CardStack draws the card frame AND the title — no inner
  // frame, no second <h3> repeating the card's own title.
  const techCardSection = activeProfileId ? (
    <MyTechnicalCard bandId={activeProfileId} />
  ) : null;

  // My calendar card (SPEC-PERSONAL-CALENDAR, 2026-10-21): the user's OWN .ics
  // subscription — link + copy + rotate (the old token dies) + the Calendar
  // page. EVERY role sees it: the feed returns only the user's own shows.
  const myCalendarSection = (
    <div data-account-my-calendar>
      {calLink && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-primary" onClick={copyCalendarLink}>
              {calCopied ? t('myCalendar.linkCopied') : t('myCalendar.copyLink')}
            </button>
            <a
              className="btn btn-secondary"
              style={{ textDecoration: 'none' }}
              href={calLink.webcal_url}
            >
              {t('myCalendar.subscribe')}
            </a>
            <button className="btn btn-secondary" onClick={rotateCalendarLink} disabled={calRotating}>
              {t('myCalendar.rotate')}
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/tour/calendar')}>
              {t('myCalendar.openPage')}
            </button>
          </div>
          <code style={{ display: 'block', marginTop: 10, fontSize: '0.78rem', color: 'var(--text-dim)', wordBreak: 'break-all' }}>
            {`${window.location.origin}${calLink.url}`}
          </code>
        </>
      )}
      {!calLink && (
        <p style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>{t('myCalendar.linkUnavailable')}</p>
      )}
    </div>
  );

  const ACCOUNT_CARDS: StackCard[] = [
    // LOT E1 (2026-11-02): `hasData` = the card has real content to show.
    // Forms and one-shot actions (password, export, delete) and the technical
    // card (an editor, not a record) stay closed; `signedin` is ALWAYS true
    // (the account itself), `twofa` only once 2FA is enabled.
    { id: 'pw', hasData: false, title: t('account.changePassword'), node: (
<div data-account-password>
  {done && (
    <div style={{ color: 'var(--success)', marginBottom: 12 }}>
      {t('account.passwordUpdated')}
    </div>
  )}
  <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    <div>
      <label>{t('account.currentPassword')}</label>
      <PasswordInput className="form-input" value={current} required
        onChange={(e) => setCurrent(e.target.value)} />
    </div>
    <div>
      <label>{t('account.newPassword')}</label>
      <PasswordInput className="form-input" value={next} required
        onChange={(e) => setNext(e.target.value)} />
      <small style={{ color: 'var(--text-dim)' }}>
        {t('account.pwHint')}
      </small>
    </div>
    <div>
      <label>{t('account.confirmNewPassword')}</label>
      <PasswordInput className="form-input" value={confirm} required
        onChange={(e) => setConfirm(e.target.value)} />
    </div>
    {error && <div style={{ color: 'var(--danger)', fontSize: '0.9rem' }}>{error}</div>}
    <button className="btn btn-primary" type="submit" disabled={busy}>
      {busy ? t('account.saving') : t('account.changePasswordBtn')}
    </button>
  </form>
</div>
    ) },
    { id: 'mycard', hasData: teamLoaded && team != null, title: t('account.myCard'), sub: t('account.teamHint'), node: (
<div data-account-my-people>
  {teamCardSection}
</div>
    ) },
    { id: 'docs', hasData: passports.length + visas.length + airlines.length > 0, title: t('account.documents'), sub: t('account.docsHint'), node: (
<div>
  {docsMsg && <div style={{ color: 'var(--success)', marginBottom: 8 }}>{docsMsg}</div>}
  {docsErr && <div style={{ color: 'var(--danger)', fontSize: '0.9rem', marginBottom: 8 }}>{docsErr}</div>}

  {teamLoaded && team == null && (
    <p style={{ color: 'var(--text-dim)' }}>{t('account.linkTeamFirst')}</p>
  )}

  {docsLoaded && team != null && (
    <>
      {/* Passports */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
        <strong>{t('account.passports', { count: passports.length })}</strong>
        <button className="btn btn-primary btn-small" onClick={addPassport}>{t('account.addPassport')}</button>
      </div>
      {passports.length === 0 && <p style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>{t('account.noPassport')}</p>}
      {passports.map((p) => (
        <div key={p.id} className="rc-item-card" style={{ padding: 12, margin: '8px 0' }}>
          {/* PASSPORT SCAN AT THE TOP of the card (same layout as
              TeamEditPage): the self-service scan route
              (/api/me/team/documents/…) serves the encrypted scan to
              the owner's own browser only; the Auto-fill button runs
              the OCR locally and proposes the values. */}
          <DocScanField
            kind="passport"
            docId={p.id}
            self
            scanPath={p.scan_path}
            hasScan={p.has_scan}
            canEdit
            onChanged={(sp) => patchPassport(p.id, { scan_path: sp })}
          />
          {(p.scan_path || p.has_scan) && (
            <div style={{ margin: '6px 0 10px' }}>
              <button className="btn btn-secondary btn-small" data-passport-ocr
                disabled={!!ocrBusy[p.id]}
                onClick={() => autoFillPassport(p)}>
                {ocrBusy[p.id] ? t('teamEdit.autoFillBusy') : t('teamEdit.autoFillScan')}
              </button>
              {ocrMsg[p.id] && (
                <small style={{ display: 'block', marginTop: 4, color: 'var(--text-dim)' }}>{ocrMsg[p.id]}</small>
              )}
              {ocrDraft[p.id] && (
                <div style={{ marginTop: 6, padding: '6px 8px', border: '1px solid var(--border, #ccc)', borderRadius: 6 }}>
                  <small style={{ color: 'var(--text-dim)' }}>
                    {t('teamEdit.autoFilledBadge', { method: 'MRZ' })}
                  </small>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <button className="btn btn-primary btn-small" onClick={() => applyPassportDraft(p)}>{t('teamEdit.applyAutoFill')}</button>
                    <button className="btn btn-secondary btn-small" onClick={() => discardPassportDraft(p.id)}>{t('teamEdit.discardAutoFill')}</button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.passportNumber')}{ocrDraft[p.id]?.fields.passport_number !== undefined ? ` ⚡` : ''}</label>
              <input className="form-input" value={passportValue(p, 'passport_number')}
                onChange={(e) => patchPassportField(p, 'passport_number', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.nationality')}{ocrDraft[p.id]?.fields.nationality !== undefined ? ` ⚡` : ''}</label>
              <CountryInput value={passportValue(p, 'nationality')}
                onChange={(v) => patchPassportField(p, 'nationality', v)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.issuingCountry')}{ocrDraft[p.id]?.fields.issuing_country !== undefined ? ` ⚡` : ''}</label>
              <CountryInput value={passportValue(p, 'issuing_country')}
                onChange={(v) => patchPassportField(p, 'issuing_country', v)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.issueDate')}{ocrDraft[p.id]?.fields.issue_date !== undefined ? ` ⚡ · ${t('teamEdit.autoFillEstimated')}` : ''}</label>
              <input className="form-input" type="date" value={passportValue(p, 'issue_date').slice(0, 10)}
                onChange={(e) => patchPassportField(p, 'issue_date', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.expiryDate')}{ocrDraft[p.id]?.fields.expiry_date !== undefined ? ` ⚡` : ''}</label>
              <input className="form-input" type="date" value={(p.expiry_date || '').slice(0, 10)}
                onChange={(e) => patchPassportField(p, 'expiry_date', e.target.value)} />
            </div>
          </div>
          <button className="btn btn-small btn-danger" onClick={() => removePassport(p.id)}>{t('account.delete')}</button>
        </div>
      ))}

      {/* Visas */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
        <strong>{t('account.visas', { count: visas.length })}</strong>
        <button className="btn btn-primary btn-small" onClick={addVisa}>{t('account.addVisa')}</button>
      </div>
      {visas.length === 0 && <p style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>{t('account.noVisa')}</p>}
      {visas.map((v) => (
        <div key={v.id} className="rc-item-card" style={{ padding: 12, margin: '8px 0' }}>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.country')}</label>
              <input className="form-input" value={v.country || ''}
                onChange={(e) => patchVisa(v.id, { country: e.target.value })} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.type')}</label>
              <input className="form-input" value={v.visa_type || ''}
                onChange={(e) => patchVisa(v.id, { visa_type: e.target.value })} placeholder={t('account.typePh')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.number')}</label>
              <input className="form-input" value={v.visa_number || ''}
                onChange={(e) => patchVisa(v.id, { visa_number: e.target.value })} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.entries')}</label>
              <select className="form-input" value={v.entry_type || 'single'}
                onChange={(e) => patchVisa(v.id, { entry_type: e.target.value as 'single' | 'multiple' })}>
                <option value="single">{t('account.single')}</option>
                <option value="multiple">{t('account.multiple')}</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.expiryDate')}</label>
              <input className="form-input" type="date" value={(v.expiry_date || '').slice(0, 10)}
                onChange={(e) => patchVisa(v.id, { expiry_date: e.target.value })} />
            </div>
          </div>
          {/* Own visa scan (image or multi-page PDF), self-service. */}
          <DocScanField
            kind="visa"
            docId={v.id}
            self
            scanPath={v.scan_path}
            hasScan={v.has_scan}
            canEdit
            onChanged={(sp) => patchVisa(v.id, { scan_path: sp })}
          />
          <button className="btn btn-small btn-danger" onClick={() => removeVisa(v.id)}>{t('account.delete')}</button>
        </div>
      ))}

      {/* Frequent flyer / loyalty programs */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
        <strong>{t('account.frequentFlyer', { count: airlines.length })}</strong>
        <button className="btn btn-primary btn-small" onClick={addAirline}>{t('account.addProgram')}</button>
      </div>
      {airlines.length === 0 && <p style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>{t('account.noLoyalty')}</p>}
      {airlines.map((a) => (
        <div key={a.id} className="rc-item-card" style={{ padding: 12, margin: '8px 0' }}>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.company')}</label>
              <AirlineCompanyField
                value={a.company || ''}
                onChange={(v) => patchAirline(a.id, { company: v })}
                onAlliance={(al) => patchAirline(a.id, { alliance: al })}
                datalistId={`airline-sug-${a.id}`}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.alliance')}</label>
              <input className="form-input" value={a.alliance || ''}
                onChange={(e) => patchAirline(a.id, { alliance: e.target.value })}
                placeholder={t('account.alliancePh')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.programNumber')}</label>
              <input className="form-input" value={a.program_number || ''}
                onChange={(e) => patchAirline(a.id, { program_number: e.target.value })} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('account.tier')}</label>
              <input className="form-input" value={a.tier || ''}
                onChange={(e) => patchAirline(a.id, { tier: e.target.value })} placeholder={t('account.tierPh')} />
            </div>
          </div>
          <button className="btn btn-small btn-danger" onClick={() => removeAirline(a.id)}>{t('account.delete')}</button>
        </div>
      ))}
    </>
  )}
</div>
    ) },
    { id: 'signedin', hasData: true, title: t('account.identityTitle'), node: (
<div>
  <p style={{ marginTop: 0, color: 'var(--text-dim)' }}>
    <Trans i18nKey="account.signedInAs" values={{ username: user?.username }} />
  </p>

  {canUseAi && (
    <>
    <h3>{t('account.aiCredits')}</h3>
    <p style={{ marginTop: 0, color: 'var(--text-dim)' }}>
      {t('account.aiCreditsHint')}
    </p>
    {aiCredits === null ? (
      <p style={{ color: 'var(--text-dim)' }}>{t('common.loading')}</p>
    ) : aiCredits.unlimited ? (
      <p style={{ fontSize: '1.4rem', fontWeight: 700, marginTop: 4 }}>
        {t('account.creditsUnlimited', { used: aiCredits.consumed })}
      </p>
    ) : (
      <>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
          {aiCredits.plan !== 'trial' && (
          <div
            className="ai-credit-pool"
            style={{ flex: '1 1 190px', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }}
          >
            <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>
              {t('account.creditsAllowance', { plan: planLabel(aiCredits.plan) })}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, margin: '2px 0' }}>{aiCredits.allowance}</div>
            <div style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>
              {t('account.creditsResetOn', { date: nextResetLabel(aiCredits.plan_cycle) })}
            </div>
          </div>
          )}
          <div
            className="ai-credit-pool"
            style={{ flex: '1 1 190px', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }}
          >
            <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>
              {aiCredits.plan === 'trial' ? t('account.creditsTrial') : t('account.creditsTopup')}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, margin: '2px 0' }}>{aiCredits.topup}</div>
            <div style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>
              {aiCredits.plan === 'trial' ? t('account.creditsTrialHint') : t('account.creditsPersistent')}
            </div>
          </div>
        </div>
        {aiCredits.total <= 0 && (
          <p style={{ color: 'var(--danger)', fontWeight: 600, marginBottom: 0 }}>
            {t('account.outOfCredits')}
          </p>
        )}
      </>
    )}

    {/* One-time credit top-up packs (Stripe mode: payment) */}
    {!aiCredits?.unlimited && creditPacks && creditPacks.packs.length > 0 && (
      <div style={{ marginTop: 16 }}>
        {topupMsg && <div style={{ color: 'var(--success)', marginBottom: 8 }}>{topupMsg}</div>}
        {topupErr && <div style={{ color: 'var(--danger)', fontSize: '0.9rem', marginBottom: 8 }}>{topupErr}</div>}
        <div style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
          {t('billing.buyCreditsHint')}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
          {creditPacks.packs.map((p) => (
            <div
              key={p.id}
              className="ai-credit-pool"
              style={{
                flex: '1 1 140px', padding: 12, borderRadius: 8, textAlign: 'center',
                border: '1px solid var(--border)',
              }}
            >
              <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{p.credits}</div>
              <div style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>{t('billing.creditsUnit')}</div>
              <div style={{ color: 'var(--text-dim)', fontSize: '0.9rem', margin: '4px 0 8px' }}>
                {formatPackPrice(p.price)}
              </div>
              <button
                type="button"
                className="btn btn-primary btn-small"
                disabled={buyingPack !== null}
                onClick={() => void buyCredits(p.id)}
                style={{ width: '100%' }}
              >
                {buyingPack === p.id ? t('common.loading') : t('billing.buy')}
              </button>
            </div>
          ))}
        </div>
      </div>
    )}
    </>
  )}

  {/* Subscription status — discreet. Pricing & billing live on the site;
      the app only links to the Stripe Customer Portal (active sub) or to
      the site for plans. No purchase UI here. */}
  {billing && (
    <p style={{ marginTop: 12, marginBottom: 0, color: 'var(--text-dim)', fontSize: '0.9rem' }}>
      {hasActiveBilling ? (
        <>
          <strong style={{ color: 'var(--text)' }}>
            {t('account.planLabel')}: {planLabel(billingSub!.plan)}
            {billingTier ? ` · ${t('billing.currentQuota', { n: billingTier.allowance })}` : ''}
          </strong>
          {' · '}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); openBillingPortal(); }}
            style={{ cursor: 'pointer' }}
          >
            {t('account.manageSub')} →
          </a>
        </>
      ) : (
        <>
          <strong style={{ color: 'var(--text)' }}>
            {t('account.planLabel')}: {t('account.planFree')}
          </strong>
          {' · '}
          <a href={SITE_URL} target="_blank" rel="noopener noreferrer">
            {t('account.viewPlans')} →
          </a>
        </>
      )}
      {billingErr && <span style={{ color: 'var(--danger)', marginLeft: 8 }}>{billingErr}</span>}
    </p>
  )}
</div>
    ) },
    { id: 'tech', hasData: false, title: t('myTech.title'), node: techCardSection },
    { id: 'calendar', hasData: !!calLink, title: t('myCalendar.accountTitle'), sub: t('myCalendar.accountHint'), node: myCalendarSection },
    { id: 'avail', hasData: avail.length > 0, title: t('availability.title'), sub: t('availability.hint'), node: (
<div>
  {/* ---- My availability (unavailability declared per band, V1) ---- */}
  {!availLoaded && <p style={{ color: 'var(--text-dim)' }}>{t('common.loading')}</p>}

  {availLoaded && team == null && (
    <p style={{ color: 'var(--text-dim)' }}>{t('account.linkTeamFirst')}</p>
  )}

  {availLoaded && team != null && (
    <>
      {availMsg && <div style={{ color: 'var(--success)', marginBottom: 8 }}>{availMsg}</div>}
      {availErr && <div style={{ color: 'var(--danger)', fontSize: '0.9rem', marginBottom: 8 }}>{availErr}</div>}

      {avail.length === 0 && (
        <p style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>{t('availability.none')}</p>
      )}

      {avail.map((a) => (
        <div
          key={a.id}
          className="rc-item-card"
          style={{ padding: '10px 14px', margin: '6px 0', display: 'flex', alignItems: 'center', gap: 12 }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 500 }}>
              {a.date_from} → {a.date_to}
              {availBands.length > 1 && a.band_name ? <span style={{ color: 'var(--text-dim)' }}> · {a.band_name}</span> : null}
            </div>
            {a.reason && (
              <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>{a.reason}</div>
            )}
          </div>
          <button className="btn btn-small btn-danger" onClick={() => removeAvailability(a)}>
            {t('account.delete')}
          </button>
        </div>
      ))}

      {/* Add form */}
      <div className="form-row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
        {availBands.length > 1 && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>{t('availability.band')}</label>
            <select className="form-input" value={newBand} onChange={(e) => setNewBand(e.target.value)}>
              {availBands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <div className="form-group" style={{ flex: 1 }}>
          <label>{t('availability.from')}</label>
          <input className="form-input" type="date" value={newFrom}
            onChange={(e) => setNewFrom(e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>{t('availability.to')}</label>
          <input className="form-input" type="date" value={newTo}
            onChange={(e) => setNewTo(e.target.value)} />
        </div>
      </div>
      <div className="form-field" style={{ marginTop: 10 }}>
        <label>{t('availability.reason')}</label>
        <AutoTextarea className="form-input" rows={2} value={newReason}
          placeholder={t('availability.reasonPh')}
          onChange={(e) => setNewReason(e.target.value)} />
      </div>
      <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={addAvailability} disabled={availSaving}>
        {availSaving ? t('account.saving') : t('availability.add')}
      </button>
    </>
  )}
</div>
    ) },
    { id: 'charts', hasData: myCharts.length > 0, title: t('songCharts.mySongCharts'), sub: t('songCharts.mySongChartsHint'), node: (
<div>
  {/* ---- My song charts (Smart Setlist V2, per-account view) ---- */}
  {!teamLoaded && <p style={{ color: 'var(--text-dim)' }}>{t('common.loading')}</p>}

  {teamLoaded && team == null && (
    <p style={{ color: 'var(--text-dim)' }}>{t('account.linkTeamFirst')}</p>
  )}

  {teamLoaded && team != null && !myChartsLoaded && (
    <p style={{ color: 'var(--text-dim)' }}>{t('common.loading')}</p>
  )}

  {teamLoaded && team != null && myChartsLoaded && myCharts.length === 0 && (
    <p style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>{t('songCharts.myEmpty')}</p>
  )}

  {teamLoaded && team != null && myChartsLoaded && myCharts.length > 0 && (
    <div>
      {groupMyCharts(myCharts).map((g) => (
        <div key={`${g.band_id}:${g.song_id}`} className="rc-item-card" style={{ padding: '10px 14px', margin: '8px 0' }}>
          <div style={{ fontWeight: 600 }}>
            {g.song_title || '—'}
            {g.band_name ? (
              <span style={{ color: 'var(--text-dim)', fontWeight: 400, fontSize: '0.85rem', marginLeft: 8 }}>
                · {g.band_name}
              </span>
            ) : null}
          </div>
          {g.instruments.map((it) => (
            <div key={it.instrument} style={{ marginTop: 10 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-dim)' }}>
                🎼 {it.instrument}
              </div>
              {it.content && (
                <pre
                  style={{
                    margin: '4px 0 0', whiteSpace: 'pre-wrap', fontFamily: 'inherit',
                    fontSize: '0.9rem', color: 'var(--text)', borderLeft: '2px solid var(--border)',
                    paddingLeft: 10,
                  }}
                >
                  {it.content}
                </pre>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  )}
</div>
    ) },
    { id: 'twofa', hasData: twofa?.enabled === true, title: t('account.twofa'), sub: t('account.twofaHint'), node: (
<div>
  {twofaMsg && <div style={{ color: 'var(--success)', marginBottom: 8 }}>{twofaMsg}</div>}
  {twofaErr && <div style={{ color: 'var(--danger)', fontSize: '0.9rem', marginBottom: 8 }}>{twofaErr}</div>}

  {!setup && (
    <div style={{ marginBottom: 12 }}>
      <strong>{t('account.status')}</strong>
      {twofa == null ? t('common.loading') : twofa.enabled ? t('account.enabled2fa') : t('account.notEnabled')}
    </div>
  )}

  {setup && (
    <div style={{ marginBottom: 12 }}>
      <ol style={{ paddingLeft: 20, marginBottom: 12 }}>
        <li>{t('account.twofaStep1')}</li>
        <li>{t('account.twofaStep2')}</li>
        <li>{t('account.twofaStep3')}</li>
      </ol>
      <div
        style={{
          border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12,
          display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
        }}
      >
        <img
          src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(setup.otpauth_url)}`}
          alt={t('account.qrAlt')}
          width={128} height={128}
          style={{ border: '1px solid var(--border)', borderRadius: 6 }}
        />
        <div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{t('account.manualSecret')}</div>
          <code style={{ fontSize: '0.9rem', wordBreak: 'break-all' }}>{setup.secret}</code>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{t('account.backupCodes')}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
          {setup.backup_codes.map((c) => (
            <code key={c} style={{ fontSize: '0.85rem', background: 'var(--bg-elevated)', padding: '2px 6px', borderRadius: 4 }}>{c}</code>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="form-input"
          style={{ width: 140 }}
          inputMode="numeric"
          placeholder={t('account.codePh')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <button className="btn btn-primary" onClick={enable2fa}>{t('account.enable2fa')}</button>
        <button className="btn btn-secondary" onClick={() => setSetup(null)}>{t('common.cancel')}</button>
      </div>
    </div>
  )}

  {!setup && !twofa?.enabled && (
    <button className="btn btn-secondary" onClick={startSetup}>{t('account.setUp2fa')}</button>
  )}

  {!setup && twofa?.enabled && (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        className="form-input"
        style={{ width: 140 }}
        inputMode="numeric"
        placeholder={t('account.currentCodePh')}
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      <button className="btn btn-danger" onClick={disable2fa}>{t('account.disable2fa')}</button>
    </div>
  )}
</div>
    ) },
    { id: 'export', hasData: false, title: t('account.exportMyData'), sub: t('account.exportHint'), node: (
<div>
  <button className="btn btn-secondary" onClick={exportMyData} disabled={gdprBusy}>
    {gdprBusy ? t('account.preparingExport') : t('account.exportBtn')}
  </button>
  {gdprMsg && <div style={{ color: 'var(--success)', marginTop: 12 }}>{gdprMsg}</div>}
  {gdprErr && <div style={{ color: 'var(--danger)', fontSize: '0.9rem', marginTop: 12 }}>{gdprErr}</div>}
</div>
    ) },
    { id: 'sessions', hasData: sessions.length > 0, title: t('account.activeSessions'), sub: t('account.sessionsHint'), node: (
<div>
  {sessionsMsg && <div style={{ color: 'var(--success)', marginBottom: 8 }}>{sessionsMsg}</div>}
  {sessionsErr && <div style={{ color: 'var(--danger)', fontSize: '0.9rem', marginBottom: 8 }}>{sessionsErr}</div>}

  <button
    className="btn btn-secondary"
    onClick={logoutAllOtherDevices}
    disabled={revoking || !sessionsLoaded || sessions.length <= 1}
    style={{ marginBottom: 12 }}
  >
    {revoking ? t('account.loggingOut') : t('account.logoutAllOther')}
  </button>

  {!sessionsLoaded && <p style={{ color: 'var(--text-dim)' }}>{t('common.loading')}</p>}

  {sessionsLoaded && sessions.length === 0 && (
    <p style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>{t('account.noSessions')}</p>
  )}

  {sessions.map((s) => (
    <div
      key={s.id}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div>
          <strong>{s.device || t('account.unknownDevice')}</strong>
          {s.this_device && <em style={{ color: 'var(--text-dim)' }}> · {t('account.thisDevice')}</em>}
        </div>
        <small style={{ color: 'var(--text-dim)' }}>
          {formatSessionDate(s.created_at)}
        </small>
      </div>
      {!s.this_device && (
        <button
          className="btn btn-small"
          onClick={() => logoutSession(s.id)}
          disabled={revoking}
          style={{ whiteSpace: 'nowrap' }}
        >
          {t('account.logout')}
        </button>
      )}
    </div>
  ))}
</div>
    ) },
    { id: 'delete', hasData: false, title: t('account.deleteMyAccount'), sub: t('account.deleteAccountHint'), node: (
<div>
  <div className="form-group">
    <label>{t('account.typeUsername')}</label>
    <input
      className="form-input"
      value={eraseConfirm}
      onChange={(e) => setEraseConfirm(e.target.value)}
      placeholder={user?.username || ''}
    />
  </div>
  <button
    className="btn"
    style={{ background: 'transparent', border: '1px solid var(--danger)', color: 'var(--danger)' }}
    onClick={deleteMyAccount}
    disabled={eraseBusy}
  >
    {eraseBusy ? t('account.deleting') : t('account.deleteBtn')}
  </button>
  {eraseErr && <div style={{ color: 'var(--danger)', fontSize: '0.9rem', marginTop: 12 }}>{eraseErr}</div>}
</div>
    ) },
  ];
  return (
    <div className="page" data-page="account" data-density="socle">
      <PageHeader
        title={withPageIcon('account.title', t('account.title'))}
        actions={
          <button className="btn btn-small" onClick={() => navigate(-1)}>{t('account.back')}</button>
        }
      />

      <div className="account-sections">
        <CardStack pageId="account" cards={ACCOUNT_CARDS} foldable defaultOpen={['signedin']} />
      </div>
    </div>
  );
}
